import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { roomService } from '../services/roomService';
import toast from 'react-hot-toast';

const { FiSend, FiLoader, FiUpload, FiFile, FiImage, FiX, FiCommand } = FiIcons;

export default function ClipboardInput({ onAddClipboardItem, isConnected, roomName }) {
  const [content, setContent] = useState('');
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const fileRef = useRef();
  const textareaRef = useRef();

  const isMac = typeof window !== 'undefined' && navigator.platform.toUpperCase().indexOf('MAC') >= 0;

  const handleShare = async () => {
    if ((!content.trim() && !file) || !isConnected || loading) return;
    
    setLoading(true);
    try {
      let payload;
      if (file) {
        // Enforce 10MB limit on frontend as well
        if (file.size > 10 * 1024 * 1024) {
          throw new Error('File size exceeds 10MB limit');
        }

        const upload = await roomService.uploadFile(roomName, file);
        if (!upload.success) throw new Error(upload.error);
        
        payload = {
          type: file.type.startsWith('image/') ? 'image' : 'file',
          content: upload.url,
          fileName: upload.name,
          fileSize: upload.size,
          fileType: upload.type
        };
      } else {
        payload = {
          type: 'text',
          content: content.trim()
        };
      }

      const ok = await onAddClipboardItem(payload);
      if (ok) {
        setContent('');
        setFile(null);
        toast.success(payload.type === 'text' ? 'Text shared' : 'File shared');
      }
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    // Detect Cmd+Enter on Mac or Ctrl+Enter on Windows/Linux
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      handleShare();
    }
  };

  return (
    <div className="bg-white rounded-[2.5rem] shadow-xl p-6 md:p-8 border border-gray-100">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <div>
          <h3 className="text-lg font-black text-gray-900 uppercase tracking-tighter">Share Something</h3>
          <p className="text-xs text-gray-400 font-bold uppercase tracking-widest mt-1">
            Max file size: 10MB
          </p>
        </div>
        
        <div className="flex w-full md:w-auto gap-3">
          <button 
            onClick={() => fileRef.current.click()}
            className="flex-1 md:flex-none flex items-center justify-center gap-2 px-6 py-3 bg-blue-50 text-blue-600 rounded-2xl font-black text-sm uppercase tracking-widest hover:bg-blue-100 transition-all border border-blue-100"
          >
            <SafeIcon icon={FiUpload} className="w-5 h-5" />
            Upload File
            <input 
              type="file" 
              ref={fileRef} 
              className="hidden" 
              onChange={(e) => {
                const selectedFile = e.target.files[0];
                if (selectedFile && selectedFile.size > 10 * 1024 * 1024) {
                  toast.error('File exceeds 10MB limit');
                  return;
                }
                setFile(selectedFile);
              }} 
            />
          </button>
        </div>
      </div>

      <div className="relative">
        {file ? (
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-blue-50 p-8 rounded-[2rem] border-2 border-dashed border-blue-200 flex flex-col items-center justify-center text-center group relative"
          >
            <button 
              onClick={() => setFile(null)}
              className="absolute top-4 right-4 p-2 bg-white rounded-xl text-gray-400 hover:text-red-500 shadow-sm transition-colors"
            >
              <SafeIcon icon={FiX} className="w-5 h-5" />
            </button>
            <div className="w-20 h-20 bg-white rounded-3xl flex items-center justify-center text-blue-600 shadow-lg mb-4">
              <SafeIcon icon={file.type.startsWith('image/') ? FiImage : FiFile} className="w-10 h-10" />
            </div>
            <p className="font-black text-gray-900 text-lg mb-1 truncate max-w-full px-4">{file.name}</p>
            <p className="text-xs text-blue-600 font-bold uppercase tracking-widest">
              {(file.size / (1024 * 1024)).toFixed(2)} MB • Ready to share
            </p>
          </motion.div>
        ) : (
          <div className="relative">
            <textarea
              ref={textareaRef}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Paste text or links here..."
              className="w-full h-40 p-6 bg-gray-50 rounded-[2rem] border-2 border-transparent focus:border-blue-500 focus:bg-white transition-all outline-none resize-none font-medium text-gray-700 placeholder:text-gray-300"
            />
            <div className="absolute bottom-4 right-6 hidden md:flex items-center gap-1 text-[10px] font-black text-gray-300 uppercase tracking-widest pointer-events-none">
              <SafeIcon icon={FiCommand} className="w-3 h-3" />
              <span>+ Enter to share</span>
            </div>
          </div>
        )}
      </div>

      <div className="mt-6 flex flex-col sm:flex-row gap-4">
        <motion.button
          onClick={handleShare}
          disabled={loading || (!content.trim() && !file) || !isConnected}
          className="flex-1 bg-gray-900 text-white py-5 rounded-[1.5rem] font-black shadow-xl disabled:opacity-30 disabled:grayscale flex items-center justify-center gap-3 uppercase tracking-widest text-sm relative overflow-hidden group"
          whileHover={{ scale: 1.02, backgroundColor: '#000' }}
          whileTap={{ scale: 0.98 }}
        >
          {loading ? (
            <SafeIcon icon={FiLoader} className="w-5 h-5 animate-spin" />
          ) : (
            <>
              <SafeIcon icon={FiSend} className="w-5 h-5 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
              Share to Room
            </>
          )}
        </motion.button>
      </div>
    </div>
  );
}